import { useNavigate } from "react-router";
import { TISACard } from "@/components/tisa/TISACard";
import { TISAButton } from "@/components/tisa/TISAButton";
import {
  Users,
  BookOpen,
  BarChart3,
  Settings,
  Plus,
  Camera,
} from "lucide-react";

export function StaffDashboard() {
  const navigate = useNavigate();

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-child-4xl font-bold text-primary">
          Staff Dashboard 👨‍🏫
        </h1>
        <p className="text-child-lg text-muted-foreground">
          Manage students, books, and library activities
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <TISACard
          themeColor="blue"
          className="text-center"
          header={
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6 text-tisa-blue-600" />
              <span className="font-semibold text-child-lg">Students</span>
            </div>
          }
        >
          <div className="space-y-3">
            <div className="text-child-3xl font-bold text-tisa-blue-600">6</div>
            <div className="text-child-sm text-muted-foreground">
              Active students
            </div>
          </div>
        </TISACard>

        <TISACard
          themeColor="green"
          className="text-center"
          header={
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-tisa-green-600" />
              <span className="font-semibold text-child-lg">Books</span>
            </div>
          }
        >
          <div className="space-y-3">
            <div className="text-child-3xl font-bold text-tisa-green-600">
              6
            </div>
            <div className="text-child-sm text-muted-foreground">
              Books in catalog
            </div>
          </div>
        </TISACard>

        <TISACard
          themeColor="yellow"
          className="text-center"
          header={
            <div className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-tisa-yellow-600" />
              <span className="font-semibold text-child-lg">Activity</span>
            </div>
          }
        >
          <div className="space-y-3">
            <div className="text-child-3xl font-bold text-tisa-yellow-600">
              12
            </div>
            <div className="text-child-sm text-muted-foreground">
              Books borrowed
            </div>
          </div>
        </TISACard>
      </div>

      {/* Main Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Student Management */}
        <TISACard themeColor="blue">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-tisa-blue-600" />
              <div>
                <h3 className="text-child-xl font-bold">Student Management</h3>
                <p className="text-child-sm text-muted-foreground">
                  View and manage student accounts
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <TISAButton
                onClick={() => navigate("/staff/students")}
                variant="theme-blue"
                size="lg"
                className="w-full"
                icon={<Users className="h-5 w-5" />}
              >
                View All Students
              </TISAButton>

              <TISAButton
                onClick={() => navigate("/staff/students/add")}
                variant="outline"
                size="md"
                className="w-full"
                icon={<Plus className="h-4 w-4" />}
              >
                Add New Student
              </TISAButton>
            </div>
          </div>
        </TISACard>

        {/* Book Management */}
        <TISACard themeColor="green">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-tisa-green-600" />
              <div>
                <h3 className="text-child-xl font-bold">Book Management</h3>
                <p className="text-child-sm text-muted-foreground">
                  Add and manage library books
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <TISAButton
                onClick={() => navigate("/staff/books")}
                variant="theme-green"
                size="lg"
                className="w-full"
                icon={<BookOpen className="h-5 w-5" />}
              >
                View All Books
              </TISAButton>

              <div className="grid grid-cols-2 gap-2">
                <TISAButton
                  onClick={() => navigate("/staff/books/add")}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  icon={<Plus className="h-4 w-4" />}
                >
                  Add Manual
                </TISAButton>

                <TISAButton
                  onClick={() => navigate("/staff/books/scan")}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  icon={<Camera className="h-4 w-4" />}
                >
                  Scan Photo
                </TISAButton>
              </div>
            </div>
          </div>
        </TISACard>
      </div>

      {/* Recent Activity */}
      <TISACard>
        <div className="space-y-4">
          <h3 className="text-child-lg font-bold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Recent Activity
          </h3>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-tisa-green-100 flex items-center justify-center">
                  📚
                </div>
                <div>
                  <div className="font-medium">
                    Emma borrowed "The Very Hungry Caterpillar"
                  </div>
                  <div className="text-child-xs text-muted-foreground">
                    2 hours ago
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-tisa-blue-100 flex items-center justify-center">
                  ✅
                </div>
                <div>
                  <div className="font-medium">
                    Sophia returned "Charlotte's Web" on time
                  </div>
                  <div className="text-child-xs text-muted-foreground">
                    1 day ago
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-tisa-yellow-100 flex items-center justify-center">
                  🏆
                </div>
                <div>
                  <div className="font-medium">
                    Ava earned 15 TISAcoins for book summary
                  </div>
                  <div className="text-child-xs text-muted-foreground">
                    2 days ago
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TISACard>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <TISAButton
          onClick={() => navigate("/staff/reports")}
          variant="outline"
          size="md"
          className="h-16 flex-col"
          icon={<BarChart3 className="h-5 w-5" />}
        >
          Reports
        </TISAButton>

        <TISAButton
          onClick={() => navigate("/staff/settings")}
          variant="outline"
          size="md"
          className="h-16 flex-col"
          icon={<Settings className="h-5 w-5" />}
        >
          Settings
        </TISAButton>

        <TISAButton
          onClick={() => navigate("/student/books")}
          variant="outline"
          size="md"
          className="h-16 flex-col"
          icon={<BookOpen className="h-5 w-5" />}
        >
          Browse Books
        </TISAButton>

        <TISAButton
          onClick={() => navigate("/login")}
          variant="outline"
          size="md"
          className="h-16 flex-col"
        >
          Logout
        </TISAButton>
      </div>
    </div>
  );
}
